import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { products } from '../data/products';

const ProductDetails = () => {
  const { id } = useParams();
  const product = products.find(p => p.id === parseInt(id));
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');

  if (!product) {
    return <div className="min-h-screen flex items-center justify-center text-2xl font-serif">Plant not found.</div>;
  }

  return (
    <div className="min-h-screen bg-cream py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Breadcrumbs */}
        <nav className="flex text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-clay">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/shop" className="hover:text-clay">Shop</Link>
          <span className="mx-2">/</span>
          <span className="text-botanical-dark">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
          {/* Image */}
          <div className="bg-white rounded-3xl overflow-hidden shadow-sm">
            <img src={product.image} alt={product.name} className="w-full h-auto object-cover aspect-[4/5]" />
          </div>

          {/* Details */}
          <div className="flex flex-col justify-center">
            {product.category === 'bundle' && (
              <span className="text-clay text-sm font-medium tracking-wider uppercase mb-2">Curated Bundle</span>
            )}
            <h1 className="text-4xl md:text-5xl font-serif text-botanical-dark mb-4">{product.name}</h1>
            
            <div className="flex items-center gap-4 mb-6 text-sm">
              <div className="flex items-center gap-1 text-clay">
                <SafeIcon icon={FiIcons.FiStar} className="fill-current" />
                <SafeIcon icon={FiIcons.FiStar} className="fill-current" />
                <SafeIcon icon={FiIcons.FiStar} className="fill-current" />
                <SafeIcon icon={FiIcons.FiStar} className="fill-current" />
                <SafeIcon icon={FiIcons.FiStar} className="fill-current opacity-50" />
              </div>
              <span className="text-gray-500">{product.reviews} reviews</span>
            </div>

            <div className="text-3xl font-medium text-botanical mb-8">${product.price}</div>

            <p className="text-gray-600 mb-8 leading-relaxed">
              {product.description}
            </p>

            {/* Tags */}
            {product.category !== 'accessory' && (
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-cream-dark text-sm shadow-sm">
                  <SafeIcon icon={FiIcons.FiSun} className="text-clay" />
                  <span className="text-gray-700">{product.lightLevel}</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-cream-dark text-sm shadow-sm">
                  <SafeIcon icon={FiIcons.FiActivity} className="text-botanical" />
                  <span className="text-gray-700">{product.difficulty} Care</span>
                </div>
              </div>
            )}

            {/* Add to Cart */}
            <div className="flex items-center gap-4 mb-8">
              <div className="flex items-center border border-gray-300 rounded-full bg-white">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-4 py-3 text-gray-500 hover:text-clay">
                  <SafeIcon icon={FiIcons.FiMinus} />
                </button>
                <span className="w-8 text-center font-medium">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="px-4 py-3 text-gray-500 hover:text-clay">
                  <SafeIcon icon={FiIcons.FiPlus} />
                </button>
              </div>
              <button className="flex-1 bg-botanical hover:bg-botanical-dark text-white py-3.5 px-8 rounded-full font-medium transition-colors flex items-center justify-center gap-2 shadow-md">
                <SafeIcon icon={FiIcons.FiShoppingCart} /> Add to Cart
              </button>
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              <SafeIcon icon={FiIcons.FiTruck} className="text-clay" />
              <span>Ships free on orders over $50. Arrives in 3-5 business days.</span>
            </div>
          </div>
        </div>

        {/* Tabs section */}
        <div className="bg-white rounded-3xl p-8 shadow-sm">
          <div className="flex gap-8 border-b border-cream-dark mb-8">
            {['description', 'care', 'reviews'].map(tab => (
              <button 
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 text-sm font-medium uppercase tracking-wider transition-colors ${
                  activeTab === tab ? 'text-clay border-b-2 border-clay' : 'text-gray-400 hover:text-botanical-dark'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          
          <div className="text-gray-600 leading-relaxed min-h-[200px]">
             {activeTab === 'description' && (
               <p>Our plants are grown in optimal greenhouse conditions and carefully acclimated before shipping. {product.description} Each plant comes in a standard nursery pot, ready to be placed in a decorative planter of your choice.</p>
             )}
             {activeTab === 'care' && (
               product.category === 'accessory' ? <p>Care instructions not applicable for this item.</p> :
               <ul className="space-y-4">
                 <li className="flex gap-3"><SafeIcon icon={FiIcons.FiDroplet} className="text-clay shrink-0 mt-1" /> <span><strong>Water:</strong> Allow top 2 inches of soil to dry out completely before watering thoroughly.</span></li>
                 <li className="flex gap-3"><SafeIcon icon={FiIcons.FiSun} className="text-clay shrink-0 mt-1" /> <span><strong>Light:</strong> Prefers {product.lightLevel.toLowerCase()} light. Avoid direct harsh afternoon sun to prevent leaf burn.</span></li>
                 <li className="flex gap-3"><SafeIcon icon={FiIcons.FiThermometer} className="text-clay shrink-0 mt-1" /> <span><strong>Environment:</strong> Keep away from cold drafts or heating vents. Standard household humidity is fine.</span></li>
               </ul>
             )}
             {activeTab === 'reviews' && (
               <div className="space-y-6">
                 <div className="border-b border-cream-dark pb-6">
                   <div className="flex items-center gap-2 mb-2">
                     <span className="font-medium text-botanical-dark">Sarah M.</span>
                     <div className="flex text-clay text-xs"><SafeIcon icon={FiIcons.FiStar} className="fill-current"/><SafeIcon icon={FiIcons.FiStar} className="fill-current"/><SafeIcon icon={FiIcons.FiStar} className="fill-current"/><SafeIcon icon={FiIcons.FiStar} className="fill-current"/><SafeIcon icon={FiIcons.FiStar} className="fill-current"/></div>
                   </div>
                   <p className="text-sm">Arrived in perfect condition! The packaging was so secure and the plant looks incredibly healthy. Will definitely order again.</p>
                 </div>
                  <div>
                   <div className="flex items-center gap-2 mb-2">
                     <span className="font-medium text-botanical-dark">James T.</span>
                     <div className="flex text-clay text-xs"><SafeIcon icon={FiIcons.FiStar} className="fill-current"/><SafeIcon icon={FiIcons.FiStar} className="fill-current"/><SafeIcon icon={FiIcons.FiStar} className="fill-current"/><SafeIcon icon={FiIcons.FiStar} className="fill-current"/><SafeIcon icon={FiIcons.FiStar} /></div>
                   </div>
                   <p className="text-sm">Beautiful plant. Dropped one leaf in transit but recovered quickly. Very happy with my purchase.</p>
                 </div>
               </div>
             )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProductDetails;